/**
 * Weekly Summary Cache Service
 * Implements weekly caching with regeneration thresholds
 * 
 * Rules:
 * - Generate once every 7 days unless thresholds are crossed
 * - Regeneration thresholds:
 *   1. 15 new items in a single day
 *   2. Dominant topic shifts by 20 percent
 *   3. Project-forming cluster appears
 *   4. Momentum spike of +300 percent
 * - Never regenerate more than once per day
 * - Store summary text and metadata (timestamp, themes used, week window)
 */

import { supabase } from '../lib/supabase';

/**
 * Get Monday of the current week (week start)
 */
function getWeekStart(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
}

/**
 * Format date as YYYY-MM-DD
 */
function formatDate(date) {
  // Handle string dates
  if (typeof date === 'string') {
    date = new Date(date);
  }
  // Validate date
  if (!date || isNaN(date.getTime())) {
    return new Date().toISOString().split('T')[0];
  }
  return date.toISOString().split('T')[0];
}

/**
 * Get Sunday of the current week (week end)
 */
function getWeekEnd(weekStart) {
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  return end;
}

/**
 * Check if date is today
 */
function isToday(date) {
  const today = new Date();
  return formatDate(date) === formatDate(today);
}

/**
 * Get days since date
 */
function daysSince(date) {
  const now = new Date();
  const diff = now - new Date(date);
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

/**
 * Get cached weekly summary
 */
export async function getCachedWeeklySummary(userId) {
  // Wait for auth session first
  const { data: session } = await supabase.auth.getSession();
  if (!session?.session?.user?.id) return null;
  
  // Use authenticated user ID, not passed userId
  const authenticatedUserId = session.session.user.id;
  if (!authenticatedUserId) return null;

  try {
    const weekStart = getWeekStart();
    const weekStartStr = formatDate(weekStart);

    const { data, error } = await supabase
      .from('weekly_summary_cache')
      .select('*')
      .eq('user_id', authenticatedUserId)
      .eq('week_start', weekStartStr)
      .single();

    if (error) {
      // Handle missing table (404) or no cache found gracefully
      if (error.code === 'PGRST116' || error.code === 'PGRST205' || error.status === 404 || error.message?.includes('404')) {
        // No cache found or table doesn't exist yet - this is fine
        return null;
      }
      // Only log non-404 errors
      if (import.meta.env?.DEV) {
        console.warn('Error fetching cached weekly summary (non-404):', error);
      }
      return null;
    }

    return data;
  } catch (error) {
    // Silently handle errors - missing table is expected if migration hasn't run
    if (import.meta.env?.DEV && !error.message?.includes('404')) {
      console.warn('Error fetching cached weekly summary:', error);
    }
    return null;
  }
}

/**
 * Check if cache is valid (less than 7 days old)
 */
export function isCacheValid(cache) {
  if (!cache) return false;
  
  const daysOld = daysSince(cache.generated_at);
  return daysOld < 7;
}

/**
 * Check if we can regenerate today (not already regenerated today)
 */
export function canRegenerateToday(cache) {
  if (!cache) return true;
  
  // If last regeneration was today, check count
  if (isToday(cache.last_regenerated_at)) {
    return cache.regeneration_count < 1;
  }
  
  return true;
}

/**
 * Check threshold 1: 25+ new items in 48 hours with new dominant clusters
 */
export async function checkThresholdNewItems(userId, items = []) {
  try {
    const twoDaysAgo = new Date();
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
    twoDaysAgo.setHours(0, 0, 0, 0);

    const recentItems = items.filter(item => {
      const itemDate = new Date(item.created_at);
      return itemDate >= twoDaysAgo;
    });

    // Need 25+ items in 48 hours
    return recentItems.length >= 25;
  } catch (error) {
    console.error('Error checking new items threshold:', error);
    return false;
  }
}

/**
 * Check threshold 2: Dominant topic shifts by 20 percent
 */
export function checkThresholdTopicShift(cache, currentDominantTheme, currentTopicMap = {}) {
  if (!cache || !cache.dominant_theme) return false;

  try {
    const oldTheme = cache.dominant_theme;
    
    // If dominant theme changed completely, check the shift
    if (oldTheme !== currentDominantTheme) {
      // Get old theme count from cache meta or calculate from old data
      const oldThemeCount = cache.meta?.oldThemeCount || 0;
      const currentThemeCount = currentTopicMap[currentDominantTheme] || 0;
      const oldThemeCurrentCount = currentTopicMap[oldTheme] || 0;
      const totalItems = Object.values(currentTopicMap).reduce((a, b) => a + b, 0);
      
      if (totalItems > 0) {
        // Calculate percentage change for the old theme
        const oldPercent = oldThemeCount > 0 ? (oldThemeCount / (oldThemeCount + (cache.item_count || 0))) * 100 : 0;
        const newPercent = (oldThemeCurrentCount / totalItems) * 100;
        const shift = Math.abs(newPercent - oldPercent);
        
        // Also check if new theme is significantly larger
        const newThemePercent = (currentThemeCount / totalItems) * 100;
        
        // Trigger if old theme dropped by 20%+ OR new theme is 20%+ larger
        return shift >= 20 || (newThemePercent >= 20 && oldPercent < newThemePercent - 20);
      }
    }

    return false;
  } catch (error) {
    console.error('Error checking topic shift threshold:', error);
    return false;
  }
}

/**
 * Check threshold 3: Project-forming cluster appears
 * This is detected by checking for smart suggestions with type 'project_forming'
 */
export function checkThresholdProjectForming(smartSuggestions = []) {
  try {
    return smartSuggestions.some(s => 
      s.type === 'project_forming' || 
      s.reason?.toLowerCase().includes('project') ||
      s.whyShowing?.toLowerCase().includes('project')
    );
  } catch (error) {
    console.error('Error checking project-forming threshold:', error);
    return false;
  }
}

/**
 * Check threshold 4: Momentum spike of +300 percent
 */
export function checkThresholdMomentumSpike(momentumMetrics = {}) {
  try {
    const weekDeltaPercent = momentumMetrics.weekDeltaPercent || 0;
    return weekDeltaPercent >= 300;
  } catch (error) {
    console.error('Error checking momentum spike threshold:', error);
    return false;
  }
}

/**
 * Check if any regeneration threshold is met
 * New rule: Only regenerate on 25+ items in 48h with new dominant clusters
 */
export async function shouldRegenerateSummary({
  userId,
  cache,
  items = [],
  currentDominantTheme,
  currentTopicMap = {},
  smartSuggestions = [],
  momentumMetrics = {}
}) {
  // If no cache, always generate
  if (!cache) return true;

  // If cache is older than 7 days, regenerate
  if (!isCacheValid(cache)) return true;

  // Check if we already regenerated today
  if (!canRegenerateToday(cache)) return false;

  // NEW THRESHOLD: 25+ items in 48 hours with new dominant clusters
  const threshold1 = await checkThresholdNewItems(userId, items);
  
  // Only regenerate if threshold1 is met (25+ items in 48h)
  // Other thresholds removed - we only regenerate on significant new activity
  const shouldRegen = threshold1;

  if (shouldRegen && import.meta.env?.DEV) {
    console.log('🔄 Weekly summary regeneration triggered:', {
      threshold1: threshold1 ? '25+ new items in 48h' : null
    });
  }

  return shouldRegen;
}

/**
 * Save weekly summary to cache
 */
export async function saveWeeklySummaryCache({
  userId,
  summaryData, // { teaser, summary, suggestions }
  themesUsed = [],
  dominantTheme = 'General',
  itemCount = 0,
  meta = {}
}) {
  // Wait for auth session first
  const { data: session } = await supabase.auth.getSession();
  if (!session?.session?.user?.id) {
    throw new Error('Not authenticated');
  }
  
  // Use authenticated user ID, not passed userId
  const authenticatedUserId = session.session.user.id;
  if (!authenticatedUserId || !summaryData) {
    throw new Error('Missing required parameters');
  }

  try {
    const weekStart = getWeekStart();
    const weekEnd = getWeekEnd(weekStart);
    const weekStartStr = formatDate(weekStart);
    const weekEndStr = formatDate(weekEnd);

    // Check if cache exists
    const existing = await getCachedWeeklySummary(authenticatedUserId);
    const isUpdate = existing !== null;
    const wasRegeneratedToday = isUpdate && isToday(existing.last_regenerated_at);
    const newRegenerationCount = wasRegeneratedToday 
      ? (existing.regeneration_count || 0) + 1 
      : 1;

    // Store old theme data for shift detection
    const oldThemeCount = existing ? (existing.meta?.oldThemeCount || existing.item_count || 0) : 0;
    
    const cacheData = {
      user_id: authenticatedUserId,
      summary_text: summaryData.summary || '',
      summary_data: summaryData,
      week_start: weekStartStr,
      week_end: weekEndStr,
      themes_used: themesUsed,
      dominant_theme: dominantTheme,
      item_count: itemCount,
      last_regenerated_at: new Date().toISOString(),
      regeneration_count: newRegenerationCount,
      meta: {
        ...(existing?.meta || {}),
        ...meta,
        oldThemeCount: existing?.dominant_theme === dominantTheme ? oldThemeCount : itemCount,
        last_generation_reason: wasRegeneratedToday ? 'threshold_triggered' : 'initial'
      }
    };

    if (isUpdate) {
      const { data, error } = await supabase
        .from('weekly_summary_cache')
        .update(cacheData)
        .eq('user_id', authenticatedUserId)
        .eq('week_start', weekStartStr)
        .select()
        .single();

      if (error) {
        // If table doesn't exist (404), just log and return null - migration needs to be run
        if (error.status === 404 || error.message?.includes('404') || error.code === '42P01') {
          if (import.meta.env?.DEV) {
            console.warn('⚠️ weekly_summary_cache table not found. Run migration: supabase/migrations/20250228000000_create_weekly_summary_cache.sql');
          }
          return null;
        }
        throw error;
      }
      return data;
    } else {
      const { data, error } = await supabase
        .from('weekly_summary_cache')
        .insert({
          ...cacheData,
          generated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        // If table doesn't exist (404), just log and return null - migration needs to be run
        if (error.status === 404 || error.message?.includes('404') || error.code === '42P01') {
          if (import.meta.env?.DEV) {
            console.warn('⚠️ weekly_summary_cache table not found. Run migration: supabase/migrations/20250228000000_create_weekly_summary_cache.sql');
          }
          return null;
        }
        throw error;
      }
      return data;
    }
  } catch (error) {
    // Handle missing table gracefully
    if (error.status === 404 || error.message?.includes('404') || error.code === '42P01') {
      if (import.meta.env?.DEV) {
        console.warn('⚠️ weekly_summary_cache table not found. Run migration: supabase/migrations/20250228000000_create_weekly_summary_cache.sql');
      }
      return null;
    }
    console.error('Error saving weekly summary cache:', error);
    throw error;
  }
}

/**
 * Get summary data from cache (for display)
 */
export function getSummaryFromCache(cache) {
  if (!cache) return null;

  // Return the full summary object
  if (cache.summary_data && typeof cache.summary_data === 'object') {
    return cache.summary_data;
  }

  // Fallback to text-only
  return {
    teaser: cache.summary_text?.substring(0, 100) || '',
    summary: cache.summary_text || '',
    suggestions: []
  };
}

